/**
 * Overtime Management Ana Sayfası
 * 
 * Tab'lı yapı ile fazla mesai yönetimi.
 * İki tab içerir:
 * 1. Bekleyen Talepler
 * 2. Geçmiş
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { all_routes } from '../../../router/all_routes';
import { useTranslation } from 'react-i18next';
import { Tabs, Alert, Card, Row, Col, Tooltip, Typography, Collapse, Button, message } from 'antd';
import { InfoCircleOutlined, SettingOutlined, ClockCircleOutlined, CheckCircleOutlined, RightOutlined } from '@ant-design/icons';
import FooterSection from '../../shared/components/footerSection';
import { useDepartments } from '../../../hooks/useDepartments';

// Tab içerikleri için komponentler
import OvertimePendingContent from './components/OvertimePendingContent';
import OvertimeHistoryContent from './components/OvertimeHistoryContent';
import OvertimeCreateFormModal from './components/OvertimeCreateFormModal';

// Services & Types
import overtimeService from '../../../services/overtime/overtime.service';
import { OvertimeCreateDTO } from '../../../types/overtime.types';
import { handleApiError } from '../../../utils/errorHandler';

const { TabPane } = Tabs;
const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

/**
 * Ana Fazla Mesai Yönetim Sayfası (Tab'lı)
 */
const OvertimeManagement: React.FC = () => {
  const { t } = useTranslation();
  const routes = all_routes;
  const [activeTab, setActiveTab] = useState<string>('pending');
  const { departments } = useDepartments();

  // Modal state'leri
  const [createModalVisible, setCreateModalVisible] = useState<boolean>(false);
  const [createLoading, setCreateLoading] = useState<boolean>(false);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // Fazla mesai sistemini kullanan departmanları say
  // Backend'den 1/0 (number) olarak geldiği için truthy check yapıyoruz
  const enabledDepartments = departments.filter(dept => !!dept.overtimeRequestEnabled);
  const totalDepartments = departments.length;

  /**
   * Çalışana fazla mesai oluştur
   */
  const handleCreateOvertime = async (data: OvertimeCreateDTO) => {
    setCreateLoading(true);
    try {
      await overtimeService.createOvertimeForEmployee(data);
      
      message.success(t('overtime.createSuccess', 'Fazla mesai başarıyla oluşturuldu'));
      setCreateModalVisible(false);
      
      // Tab içeriklerini yenile
      setRefreshTrigger(prev => prev + 1);
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">
                <i className="ti ti-clock-hour-4 me-2" />
                {t('overtime.overtimeManagement', 'Fazla Mesai Yönetimi')}
              </h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">
                    {t('overtime.overtime', 'Fazla Mesai')}
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {t('overtime.management', 'Yönetim')}
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Breadcrumb */}

          {/* Bilgilendirme Bölümü */}
          <Card 
            className="mb-4"
            style={{ 
              borderLeft: '4px solid #1890ff',
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
            }}
          >
            <Row gutter={24}>
              <Col xs={24} lg={16}>
                <div className="d-flex align-items-start mb-3">
                  <InfoCircleOutlined 
                    style={{ 
                      fontSize: '32px', 
                      color: '#1890ff',
                      marginRight: '16px',
                      marginTop: '4px'
                    }} 
                  />
                  <div>
                    <Title level={4} style={{ marginBottom: '8px', marginTop: 0 }}>
                      {t('overtime.info.title', 'Fazla Mesai Talep Sistemi Hakkında')}
                    </Title>
                    <Paragraph style={{ marginBottom: '12px', fontSize: '15px' }}>
                      {t('overtime.info.description', 'Fazla mesai talep sistemi, çalışanların fazla mesai yapmak istediklerinde yöneticilerinden onay almalarını sağlar. Bu sistem sayesinde fazla mesailer kontrollü ve düzenli bir şekilde yönetilir.')}
                    </Paragraph>
                    <div className="d-flex flex-wrap gap-3">
                      <div className="d-flex align-items-center">
                        <ClockCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                        <Text>
                          {t('overtime.info.feature1', 'Çalışan talep oluşturur')}
                        </Text>
                      </div>
                      <div className="d-flex align-items-center">
                        <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                        <Text>
                          {t('overtime.info.feature2', 'Yönetici onaylar/reddeder')}
                        </Text>
                      </div>
                      <div className="d-flex align-items-center">
                        <InfoCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                        <Tooltip title={t('overtime.info.feature3Tooltip', 'Sadece yönetici onayı ile fazla mesai yapılabilir')}>
                          <Text style={{ cursor: 'help' }}>
                            {t('overtime.info.feature3', 'Kontrollü fazla mesai yönetimi')}
                          </Text>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
              <Col xs={24} lg={8}>
                <Card 
                  size="small"
                  style={{ 
                    background: 'white',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}
                >
                  <div className="text-center">
                    <SettingOutlined style={{ fontSize: '32px', color: '#1890ff', marginBottom: '12px' }} />
                    <Title level={5} style={{ marginBottom: '8px' }}>
                      {t('overtime.info.settingsTitle', 'Departman Ayarları')}
                    </Title>
                    <div className="mb-3">
                      <Text type="secondary" style={{ fontSize: '13px' }}>
                        {t('overtime.info.activeDepartments', 'Manuel talep sistemi kullanan departmanlar:')}
                      </Text>
                      <div style={{ marginTop: '8px' }}>
                        <Text strong style={{ fontSize: '24px', color: enabledDepartments.length > 0 ? '#52c41a' : '#ff4d4f' }}>
                          {enabledDepartments.length}
                        </Text>
                        <Text type="secondary" style={{ fontSize: '14px' }}> / {totalDepartments}</Text>
                      </div>
                      
                      {/* Aktif Departman İsimleri */}
                      {enabledDepartments.length > 0 && (
                        <div style={{ marginTop: '8px', fontSize: '11px', color: '#52c41a' }}>
                          {enabledDepartments.map((dept, index) => (
                            <span key={dept.id}>
                              {dept.name}
                              {index < enabledDepartments.length - 1 && ', '}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <Tooltip title={t('overtime.info.settingsTooltip', 'Departmanlar sayfasından her departman için fazla mesai talep sistemini açıp kapatabilirsiniz.')}>
                      <Link to={routes.departments}>
                        <Text 
                          style={{ 
                            color: '#1890ff',
                            cursor: 'pointer',
                            fontSize: '13px',
                            textDecoration: 'underline'
                          }}
                        >
                          <SettingOutlined style={{ marginRight: '4px' }} />
                          {t('overtime.info.goToSettings', 'Departman Ayarlarına Git')}
                        </Text>
                      </Link>
                    </Tooltip>
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Uyarı: Eğer hiçbir departmanda açık değilse */}
            {enabledDepartments.length === 0 && (
              <Alert
                message={t('overtime.info.warningTitle', '⚠️ Manuel Talep Sistemi Hiçbir Departmanda Aktif Değil')}
                description={
                  <div>
                    <Paragraph style={{ marginBottom: '8px' }}>
                      {t('overtime.info.warningDescription', 'Şu anda hiçbir departmanda manuel fazla mesai talep sistemi aktif değil. Çalışanlar fazla mesai talebi oluşturamaz.')}
                    </Paragraph>
                    <Text strong>
                      {t('overtime.info.warningAction', 'Nasıl Aktif Edilir?')}
                    </Text>
                    <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
                      <li>
                        <Link to={routes.departments}>
                          <Text style={{ color: '#1890ff' }}>
                            {t('overtime.info.step1', 'Departmanlar')}
                          </Text>
                        </Link>
                        {t('overtime.info.step1Detail', ' sayfasına gidin')}
                      </li>
                      <li>{t('overtime.info.step2', 'Bir departmanı düzenleyin')}</li>
                      <li>{t('overtime.info.step3', '"Fazla Mesai Yönetimi" bölümünden "Manuel Talep Sistemi" switch\'ini açın')}</li>
                      <li>{t('overtime.info.step4', 'Değişiklikleri kaydedin')}</li>
                    </ul>
                  </div>
                }
                type="warning"
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}
          </Card>

          {/* Detaylı Açıklama - Collapse */}
          <Collapse
            className="mb-4"
            bordered={false}
            expandIcon={({ isActive }) => <RightOutlined rotate={isActive ? 90 : 0} />}
            style={{ background: '#fafafa' }}
          >
            <Panel 
              header={
                <span style={{ fontWeight: 500, fontSize: '15px' }}>
                  <InfoCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                  {t('overtime.info.howItWorks', 'Sistem Nasıl Çalışır? (İki Farklı Mod)')}
                </span>
              } 
              key="1"
            >
              <Row gutter={16}>
                {/* Otomatik Mod */}
                <Col xs={24} md={12}>
                  <Card 
                    size="small"
                    style={{ 
                      height: '100%',
                      borderLeft: '3px solid #ff9800'
                    }}
                  >
                    <div className="mb-3">
                      <SettingOutlined style={{ fontSize: '24px', color: '#ff9800', marginRight: '8px' }} />
                      <Text strong style={{ fontSize: '16px' }}>
                        {t('overtime.info.autoMode', 'Otomatik Mod (Sistem Kapalı)')}
                      </Text>
                    </div>
                    <Paragraph style={{ marginBottom: '12px', fontSize: '13px' }}>
                      <Text type="secondary">
                        {t('overtime.info.autoModeDesc', 'Manuel talep sistemi kapalı olduğunda:')}
                      </Text>
                    </Paragraph>
                    <ul style={{ paddingLeft: '20px', margin: 0, fontSize: '13px' }}>
                      <li style={{ marginBottom: '8px' }}>
                        {t('overtime.info.autoMode1', 'Çalışan normal çıkış saatinden sonra çalışmaya devam eder')}
                      </li>
                      <li style={{ marginBottom: '8px' }}>
                        <Text strong>{t('overtime.info.autoMode2', 'Çıkış yapınca sistem otomatik hesaplar')}</Text>
                      </li>
                      <li style={{ marginBottom: '8px' }}>
                        {t('overtime.info.autoMode3', 'Mesai süresi aşımı otomatik fazla mesai olarak kaydedilir')}
                      </li>
                      <li style={{ marginBottom: '8px' }}>
                        <Text type="danger">{t('overtime.info.autoMode4', 'Yönetici onayı gerekmez')}</Text>
                      </li>
                      <li>
                        {t('overtime.info.autoMode5', 'Puantaja direkt yansır')}
                      </li>
                    </ul>
                    <Alert
                      message={t('overtime.info.autoModeWarning', '⚠️ Kontrol yetkisi sınırlıdır')}
                      type="warning"
                      showIcon={false}
                      style={{ marginTop: '12px', fontSize: '12px' }}
                    />
                  </Card>
                </Col>

                {/* Manuel Talep Modu */}
                <Col xs={24} md={12}>
                  <Card 
                    size="small"
                    style={{ 
                      height: '100%',
                      borderLeft: '3px solid #52c41a'
                    }}
                  >
                    <div className="mb-3">
                      <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a', marginRight: '8px' }} />
                      <Text strong style={{ fontSize: '16px' }}>
                        {t('overtime.info.manualMode', 'Manuel Talep Modu (Sistem Açık)')}
                      </Text>
                    </div>
                    <Paragraph style={{ marginBottom: '12px', fontSize: '13px' }}>
                      <Text type="secondary">
                        {t('overtime.info.manualModeDesc', 'Manuel talep sistemi açık olduğunda:')}
                      </Text>
                    </Paragraph>
                    <ul style={{ paddingLeft: '20px', margin: 0, fontSize: '13px' }}>
                      <li style={{ marginBottom: '8px' }}>
                        {t('overtime.info.manualMode1', 'Çalışan fazla mesai yapmadan ÖNCE veya SONRA talep gönderebilir')}
                      </li>
                      <li style={{ marginBottom: '8px' }}>
                        {t('overtime.info.manualMode2', 'Yönetici talebi inceler ve onaylar/reddeder')}
                      </li>
                      <li style={{ marginBottom: '8px' }}>
                        <Text type="success">{t('overtime.info.manualMode3', 'Sadece onaylanan talepler geçerlidir ve puantaja yansır')}</Text>
                      </li>
                      <li style={{ marginBottom: '8px' }}>
                        <Text type="danger">{t('overtime.info.manualMode4', 'Onaylanmayan talepler sisteme işlenmez')}</Text>
                      </li>
                      <li style={{ marginBottom: '8px' }}>
                        {t('overtime.info.manualMode5', 'Mesai süresi aşımları otomatik KAYDA ALINMAZ')}
                      </li>
                      <li style={{ marginBottom: '8px' }}>
                        <Text strong>{t('overtime.info.manualMode6', 'Talep yoksa çıkış saatinde otomatik çıkış yapılır')}</Text>
                      </li>
                      <li>
                        {t('overtime.info.manualMode7', 'Kontrollü ve düzenli fazla mesai yönetimi')}
                      </li>
                    </ul>
                    <Alert
                      message={t('overtime.info.manualModeSuccess', '✓ Tam kontrol ve şeffaflık')}
                      type="success"
                      showIcon={false}
                      style={{ marginTop: '12px', fontSize: '12px' }}
                    />
                  </Card>
                </Col>
              </Row>

              {/* Alt Bilgi */}
              <Alert
                message={
                  <div>
                    <Text strong>{t('overtime.info.switchInfo', '💡 Nasıl Değiştirilir?')}</Text>
                    <div style={{ marginTop: '8px', fontSize: '13px' }}>
                      {t('overtime.info.switchInfoDesc', 'Departmanlar sayfasından her departman için ayrı ayrı "Manuel Talep Sistemi" açık/kapalı yapabilirsiniz. Her departman kendi ihtiyacına göre çalışır.')}
                    </div>
                  </div>
                }
                type="info"
                style={{ marginTop: '16px' }}
              />
            </Panel>
          </Collapse>

          {/* Tabs */}
          <div className="card">
            <div className="card-body">
              <Tabs 
                activeKey={activeTab} 
                onChange={setActiveTab}
                size="large"
                type="card"
                tabBarExtraContent={
                  <Button
                    type="primary"
                    icon={<i className="ti ti-plus me-2" />}
                    onClick={() => setCreateModalVisible(true)}
                    style={{ backgroundColor: '#6f42c1', borderColor: '#6f42c1' }}
                  >
                    {t('overtime.createForEmployee', 'Çalışana Mesai Oluştur')}
                  </Button>
                }
              >
                {/* Bekleyen Talepler Tab */}
                <TabPane
                  tab={
                    <span>
                      <i className="ti ti-clock-hour-4 me-2" />
                      {t('overtime.pendingRequests', 'Bekleyen Talepler')}
                    </span>
                  }
                  key="pending"
                >
                  <OvertimePendingContent key={`pending-${refreshTrigger}`} />
                </TabPane>

                {/* Geçmiş Tab */}
                <TabPane
                  tab={
                    <span>
                      <i className="ti ti-history me-2" />
                      {t('overtime.history', 'Geçmiş')}
                    </span>
                  }
                  key="history"
                >
                  <OvertimeHistoryContent key={`history-${refreshTrigger}`} />
                </TabPane>
              </Tabs>
            </div>
          </div>

          {/* Çalışana Fazla Mesai Oluştur Modal */}
          <OvertimeCreateFormModal
            visible={createModalVisible}
            onSubmit={handleCreateOvertime}
            onCancel={() => setCreateModalVisible(false)}
            loading={createLoading}
          />
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <FooterSection />
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default OvertimeManagement;

